import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Camera, MapPin, LogOut, Loader2, ArrowLeft } from "lucide-react";

export default function PromoterCheckOut() {
  const nav = useNavigate();
  const [coords, setCoords] = useState(null);
  const [locErr, setLocErr] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!navigator.geolocation) return setLocErr("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy }),
      (err) => setLocErr(err.message),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!coords) return toast.error("Location not available");
    if (!photo) return toast.error("Please capture a photo");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("latitude", String(coords.lat));
      fd.append("longitude", String(coords.lng));
      fd.append("photo", photo);
      await api.post("/promoter/shift/check-out", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Shift completed");
      nav("/promoter");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-neutral-500" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          End shift
        </div>
        <h1 className="font-display font-semibold text-3xl tracking-tight">Check out</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Confirm your location and capture a photo to complete the shift.
        </p>
      </div>

      <div className="glass-panel p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
          Location
        </div>
        {coords ? (
          <div className="flex items-center gap-2 font-mono text-sm">
            <MapPin className="h-4 w-4 text-emerald-600" />
            {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
            <span className="text-xs text-neutral-500 ml-1">±{Math.round(coords.acc)}m</span>
          </div>
        ) : locErr ? (
          <div className="text-sm text-red-600">{locErr}</div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Acquiring GPS…
          </div>
        )}
      </div>

      <div className="glass-panel p-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
          Photo
        </div>
        {preview ? (
          <div className="space-y-3">
            <img src={preview} alt="preview" className="w-full aspect-square object-cover glass-panel" />
            <label className="block">
              <input type="file" accept="image/*" capture="user" onChange={onFile} className="hidden" />
              <Button variant="outline" className="rounded-xl w-full" asChild>
                <span>Retake</span>
              </Button>
            </label>
          </div>
        ) : (
          <label className="block">
            <input
              type="file"
              accept="image/*"
              capture="user"
              onChange={onFile}
              className="hidden"
              data-testid="checkout-photo-input"
            />
            <div className="border-2 border-dashed border-slate-300/70 aspect-square flex flex-col items-center justify-center cursor-pointer hover:bg-white/50">
              <Camera className="h-10 w-10 text-neutral-400" />
              <div className="mt-2 text-sm text-neutral-500">Tap to capture</div>
            </div>
          </label>
        )}
      </div>

      <Button
        disabled={busy || !coords || !photo}
        onClick={submit}
        className="rounded-xl w-full h-14 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wider"
        data-testid="confirm-check-out"
      >
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : (<><LogOut className="h-5 w-5 mr-2" /> Confirm check-out</>)}
      </Button>
    </div>
  );
}
